import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { TheAccountDropdown } from '@maropost-ui/liquidsky-ui'
  import { useUserStore } from '@/lib/stores'
  import { computed, ref, type Ref } from 'vue'
  import { $accountService } from '@/lib/services/accountService'
  import { constants } from "@/lib/utils/constants"
  import { debounce } from "@/lib/utils/utils"
import { useRoute } from 'vue-router'

/**
 * Represents an account object with a unique ID.
 * Additional properties may exist (e.g., name, email, type).
 */
 interface Account {
  id: number
  [key: string]: any
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AccountsDropDown',
  setup(__props) {

  const route = useRoute();

/**
 * Accesses the authentication store instance
 * — provides user info, active accounts, and permissions.
 */
const userStore = useUserStore()

/**
 * Reactive boolean indicating whether the account data is currently loading.
 * Derived from the account service's fetching state.
 */
const isLoading = computed<boolean>(() => $accountService.isFetching.value)

/**
 * Contains the list of accounts returned from search queries.
 * Helps separate search results from the user's active accounts.
 */
const searchedAccounts: Ref<Account[]> = ref([])

/**
 * Indicates whether there are more accounts available to fetch from the API.
 * True if the number of active accounts is less than the total available.
 */
const haveMoreAccounts = ref<boolean>(
  userStore.getActiveAccounts.length < $accountService.pagination.value.totalItems
)

/**
 * Tracks the user's input for account name search.
 * Used to trigger filtering and API queries.
 */
const searchText = ref<string>("")

/**
 * Computed list of unique active accounts.
 * Merges the user's active accounts with search results,
 * ensuring no duplicates based on account `id`.
 */
const activeAccounts = computed<Account[]>(() => {
  return [
    ...new Map(
      [...userStore.getActiveAccounts, ...searchedAccounts.value].map(
        (item: any) => [item.id, item] // deduplicate by account ID
      )
    ).values(),
  ]
})

/**
 * Resets account-related data to their default states.
 * Clears search results, user menu, and resets pagination flags.
 */
const resetAccountsToDefaults = (): void => {
  searchedAccounts.value = []
  haveMoreAccounts.value =
  userStore.getActiveAccounts.length < $accountService.pagination.value.totalItems
  searchText.value = ""
}

/**
 * Handles navigation when a user selects an account.
 * Redirects the browser to the selected account’s dashboard page.
 * (Uses `window.location.assign` to ensure full page reload.)
 */
 const handleAccountChange = (accountId: number): void => {
  const path = `/accounts/${accountId}/dashboard`
  window.location.assign(path)
}

/**
 * Handles updates to the search input field.
 * Debounces user input to prevent excessive API calls.
 * Resets the current search results before loading new ones.
 */
const handleSearchChange = (): void => {
  debounce(() => {
    searchedAccounts.value = []
    haveMoreAccounts.value = true
    loadMoreAccounts()
  }, 1000)() // waits 1 second after user stops typing
}

/**
 * Loads additional accounts (pagination logic).
 * Fetches the next page of accounts based on search or active state.
 * Merges fetched accounts into the local list while avoiding duplicates.
 * Updates `haveMoreAccounts` once all pages are loaded.
 */
const loadMoreAccounts = async (): Promise<void> => {
  // Prevent duplicate requests or fetching when no more accounts are left
  if (isLoading.value || !haveMoreAccounts.value) return

  // Calculate pagination based on the current list length
  const currentPage = Math.floor(
    (searchText.value ? searchedAccounts.value.length : activeAccounts.value.length) /
      constants.accountsPerPage
  )
  const nextPage = currentPage + 1

  // Construct query params dynamically
  const params: Record<string, any> = {
    page: nextPage,
    per_page: constants.accountsPerPage,
    ...(searchText.value ? { name_cont: searchText.value } : {}), // optional filter by name
  }

  // Fetch accounts from backend
  await $accountService.findAll({ params })

  // Merge newly fetched accounts with existing ones (deduplication by ID)
  const mergedArray = new Map<number, Account>(
    [...searchedAccounts.value, ...$accountService.items.value].map(
      (item: Account) => [item.id, item]
    )
  )
  searchedAccounts.value = [...mergedArray.values()]

  // Determine if all available accounts have been fetched
  if (
    (searchText.value ? searchedAccounts.value.length : activeAccounts.value.length) >=
    $accountService.pagination.value.totalItems
  ) {
    haveMoreAccounts.value = false
  }
}



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(TheAccountDropdown), {
    "account-dropdown-class": "text-capitalize my-0 py-0 secondary--font d-none d-md-flex account-dropdown",
    "is-loading": isLoading.value,
    accounts: activeAccounts.value,
    "selected-account-id": _unref(userStore).getAccountId,
    "onUpdate:selectedAccountId": _cache[0] || (_cache[0] = ($event: any) => ((_unref(userStore).getAccountId) = $event)),
    "search-string": searchText.value,
    "onUpdate:searchString": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
    onAccountChange: handleAccountChange,
    onLoadmore: loadMoreAccounts,
    onSearchChange: handleSearchChange,
    showLoadMore: haveMoreAccounts.value,
    disableAccountDropdown: _unref(route).meta.disableAccountDropdown
  }, null, 8, ["is-loading", "accounts", "selected-account-id", "search-string", "showLoadMore", "disableAccountDropdown"]))
}
}

})